import { useEffect, useState } from 'react';

import useUIActions from '@/components/hooks/useUIActions';
import { IconButton } from '@/components/ui/Button/IconButton';
import { CheckCircleFill, Close, EmergencyHomeFill, InfoFill, WarningFill } from '@/components/ui/Icons';
import { Flex } from '@/new_src/view/components/Flex';
import { Text } from '@/new_src/view/components/Text';

import styled, { css, keyframes, useTheme } from 'styled-components';
import { defaultTheme } from '@/theme';

export const TOAST_TYPES = {
  ERROR: 'error',
  SUCCESS: 'success',
  WARNING: 'warning',
  INFO: 'info',
  NO_ICON: 'no-icon',
} as const;
export type ToastType = (typeof TOAST_TYPES)[keyof typeof TOAST_TYPES];

export interface ToastData {
  id: string;
  msg: string;
  toastType?: ToastType;
  showClose?: boolean;
  leaving?: boolean;
}

const getToastIcon = (toastType: ToastType) => {
  switch (toastType) {
    case 'success':
      return <CheckCircleFill width={20} height={20} fill={defaultTheme.mantineColors.green[5]} />;
    case 'error':
      return <EmergencyHomeFill width={20} height={20} fill={defaultTheme.mantineColors.red[5]} />;
    case 'warning':
      return <WarningFill width={20} height={20} fill={defaultTheme.mantineColors.yellow[5]} />;
    case 'info':
      return <InfoFill width={20} height={20} fill={defaultTheme.mantineColors.blue[5]} />;
    case 'no-icon':
      return null;
    default:
      return <CheckCircleFill width={20} height={20} fill={defaultTheme.mantineColors.green[5]} />;
  }
};

type ToastProps = ToastData;

const Toast = ({ id, msg, showClose = false, leaving, toastType = 'success' }: ToastProps) => {
  const [fadeIn, toggleFadeIn] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const { hideToast } = useUIActions();
  const theme = useTheme();
  const shouldShowIcon = toastType !== 'no-icon';

  // 자동 닫기 타이머
  useEffect(() => {
    const duration = 6_000; // TODO: Button 포함한 Toast는 10초로 변경 필요
    const timer = setTimeout(() => {
      toggleFadeIn(false);
    }, duration);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!leaving) {
      return;
    }
    toggleFadeIn(false);
  }, [leaving]);

  const handleAnimationEnd = () => {
    if (fadeIn) {
      return;
    }
    setCollapsed(true);
  };

  const handleTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
    if (collapsed && e.propertyName === 'max-height') {
      hideToast(id);
    }
  };

  const handleCloseToast = () => {
    toggleFadeIn(false);
  };

  return (
    <ToastSlideWrapper $fadeIn={fadeIn} onAnimationEnd={handleAnimationEnd}>
      <ToastWrapper $collapsed={collapsed} onTransitionEnd={handleTransitionEnd}>
        <ToastBox title={msg}>
          {shouldShowIcon && <ToastIconContainer>{getToastIcon(toastType)}</ToastIconContainer>}
          <Flex flex={1} miw={0} py={4}>
            <Text size="14px" fw={500} lh={1.6} c="gray.1" truncate={false}>
              {msg}
            </Text>
          </Flex>
          {showClose && (
            <IconButton
              variant="gray-dark-transparent"
              size="md"
              ariaLabel="닫기"
              onClick={handleCloseToast}
              renderIcon={({ width, height }) => (
                <Close width={width} height={height} fill={theme.mantineColors.gray[4]} />
              )}
            />
          )}
        </ToastBox>
      </ToastWrapper>
    </ToastSlideWrapper>
  );
};

const animateSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const animateSlideLeft = keyframes`
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(-100%);
  }
`;

// 슬라이드 애니메이션 + 간격 담당 — overflow:hidden 바깥에 위치해 슬라이드가 viewport 끝까지 자연스럽게 동작
const ToastSlideWrapper = styled.div<{ $fadeIn: boolean }>`
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }

  ${({ $fadeIn }) => {
    if ($fadeIn) {
      return css`
        animation: ${animateSlideIn} ease 0.3s;
      `;
    }
    return css`
      animation: ${animateSlideLeft} ease 0.3s forwards;
    `;
  }}
`;

// height collapse 담당
const ToastWrapper = styled.div<{ $collapsed: boolean }>`
  overflow: hidden;
  max-height: ${({ $collapsed }) =>
    $collapsed ? '0px' : '300px'}; // collapse 애니메이션용 — height: auto는 animate 불가하므로 충분히 큰 값 사용
  transition: max-height 0.3s ease;
`;

const ToastBox = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 8px 16px;
  width: 100%;
  min-height: 48px;
  border-radius: 8px;
  box-shadow: 0 4px 12px 0 ${({ theme }) => theme.createRGBAWithHex(theme.mantineColors.gray[8], 0.15)};
  box-sizing: border-box;
  gap: 8px;

  background-color: ${({ theme }) => theme.mantineColors.gray[8]};
`;

// 아이콘 컨테이너 h-32px: 아이콘(20px) 중앙 정렬 → 텍스트 첫 줄 중앙과 시각적으로 정렬됨
const ToastIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  height: 32px;
`;

export default Toast;
